import { __awaiter } from "tslib";
import React, { useCallback, useState } from 'react';
import { TravellerFieldEnum } from '@websky/graphql';
import { TravellerDocumentFields, TravellerFields } from '../TravellerForm/types';
import { getActualPassengerValues } from './utils';
import { convertGenderToCorrectValue, getUserValue } from '../utils';
import { formatInvalidPassengerDate } from '../Passenger/utils';
import { useToggleable } from '../hooks';
import { checkFfpStatusVariablesFactory, useFfpStatusCheck } from '../__fsd__/features/ffp-status-check';
export const reducer = (state, action) => {
    var _a, _b, _c, _d, _e;
    if (action.type === 'fill') {
        return Object.assign(Object.assign({}, state), { customer: Object.assign(Object.assign({}, ((_a = state.customer) !== null && _a !== void 0 ? _a : {})), { email: (_b = action.payload.values.get('email')) !== null && _b !== void 0 ? _b : (_c = state.customer) === null || _c === void 0 ? void 0 : _c.email, phone: (_d = action.payload.values.get('phone')) !== null && _d !== void 0 ? _d : (_e = state.customer) === null || _e === void 0 ? void 0 : _e.phone }), passengers: state.passengers.map((passenger, index) => {
                if (index === action.payload.id) {
                    const values = Object.assign(Object.assign({}, passenger), { profileId: action.payload.profileId });
                    for (const key in values) {
                        let value = values[key];
                        const isDocField = [
                            TravellerDocumentFields.DocExpiryDate,
                            TravellerDocumentFields.DocNumber
                        ].includes(key);
                        if (action.payload.values.get(key) || isDocField) {
                            value = action.payload.values.get(key);
                        }
                        values[key] = value;
                    }
                    return values;
                }
                return passenger;
            }) });
    }
    else if (action.type === 'swap') {
        return Object.assign(Object.assign({}, state), { passengers: state.passengers.map((passenger, index) => {
                if (index === action.payload.id) {
                    return Object.assign(Object.assign({}, passenger), { [TravellerFields.FirstName]: passenger[TravellerFields.LastName], [TravellerFields.LastName]: passenger[TravellerFields.FirstName] });
                }
                return passenger;
            }) });
    }
    return state;
};
export const createInitialState = (passengers, customer) => {
    var _a;
    let customerValues = {
        email: '',
        phone: '',
        accompanyingPersonName: '',
        accompanyingPersonTicketNumber: '',
        accompanyingCheck: false,
        subscribed: false
    };
    if (customer) {
        customerValues = Object.assign(Object.assign({}, customerValues), { email: getUserValue(customer, TravellerFieldEnum.Email), phone: getUserValue(customer, TravellerFieldEnum.Phone), subscribed: (_a = customer.subscribed) !== null && _a !== void 0 ? _a : false });
    }
    const passengerValues = passengers.map(passenger => {
        var _a;
        const values = {
            type: passenger.type,
            disabilities: { enabled: false, values: {} },
            files: { changeFiles: [] }
        };
        (_a = passenger.values) === null || _a === void 0 ? void 0 : _a.forEach(value => {
            var _a, _b, _c, _d;
            let actualValue = value.value;
            switch (value.type) {
                case TravellerFieldEnum.Nationality:
                    const hasValidationOption = (_c = (_b = (_a = value.validationRules) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.some(option => option.value === value.value);
                    if (!hasValidationOption && ((_d = value.validationRules) === null || _d === void 0 ? void 0 : _d.length)) {
                        actualValue = value.validationRules[0].options[0].value;
                    }
                    break;
                case TravellerFieldEnum.BirthDate:
                case TravellerFieldEnum.DocExpiryDate:
                    actualValue = formatInvalidPassengerDate(value.type, actualValue);
                    break;
                case TravellerFieldEnum.Gender:
                    actualValue = convertGenderToCorrectValue(actualValue);
                    break;
            }
            values[value.name] = actualValue;
        });
        return values;
    });
    return {
        customer: customerValues,
        passengers: passengerValues
    };
};
export const usePassengerForm = (passengers, onFormSubmit, customer, isInternationalFlight, isFreezeOrder, showCheckDataModal) => {
    const { checkFfpStatus, isLoading: isCheckFfpStatusLoading, isEnabled: isFfpStatusCheckEnabled } = useFfpStatusCheck();
    const { isOpen: isCheckDataModalOpen, open: openCheckDataModal, close: closeCheckDataModal } = useToggleable(false);
    const [formValues, setFormValues] = useState(null);
    const [state, dispatch] = React.useReducer(reducer, createInitialState(passengers, customer));
    const selectedProfileIdsByPassenger = React.useMemo(() => {
        const selectedProfileIdsByPassengerMap = new Map();
        if (state.passengers.length) {
            state.passengers.forEach((passenger, index) => {
                if (passenger.profileId) {
                    selectedProfileIdsByPassengerMap.set(index, passenger.profileId);
                }
            });
        }
        return selectedProfileIdsByPassengerMap;
    }, [state]);
    // TODO: fix passenger index to real passenger id
    const onFillValues = React.useCallback((profile, id, document) => {
        if (profile) {
            const actualPassengerValues = getActualPassengerValues(passengers, profile, id, document, isInternationalFlight, isFreezeOrder);
            dispatch({ type: 'fill', payload: { id, profileId: profile.id, values: actualPassengerValues } });
        }
    }, [state, passengers, isInternationalFlight, isFreezeOrder, dispatch]);
    const onSwapNames = React.useCallback((id) => dispatch({ type: 'swap', payload: { id } }), [state]);
    const onConfirm = (values) => __awaiter(void 0, void 0, void 0, function* () {
        if (isFfpStatusCheckEnabled) {
            const formPassengers = values.passengers;
            const passengersForCheck = checkFfpStatusVariablesFactory(passengers.map((passenger, i) => {
                var _a;
                return ({
                    travellerId: passenger.id,
                    values: passenger.values.map(field => {
                        var _a, _b;
                        return (Object.assign(Object.assign({}, field), { value: (_b = (_a = formPassengers[i]) === null || _a === void 0 ? void 0 : _a[field.name]) !== null && _b !== void 0 ? _b : null }));
                    }),
                    loyaltyCode: (_a = formPassengers[i]) === null || _a === void 0 ? void 0 : _a['loyaltyNumber']
                });
            }));
            if (passengersForCheck.length) {
                try {
                    const errors = yield checkFfpStatus(passengersForCheck);
                    if (errors) {
                        const passengersErrors = {};
                        Object.entries(errors).forEach(([travellerId, errorMsg]) => {
                            passengersErrors[travellerId] = { loyaltyNumber: errorMsg };
                        });
                        return { passengers: passengersErrors };
                    }
                }
                catch (error) {
                    return { error };
                }
            }
        }
        if (showCheckDataModal) {
            onShowCheckDataModal(values);
            return undefined;
        }
        onFormSubmit(values);
    });
    const onModalConfirm = useCallback(() => {
        closeCheckDataModal();
        onFormSubmit(formValues);
    }, [formValues]);
    const onShowCheckDataModal = useCallback((values) => {
        setFormValues(values);
        openCheckDataModal();
    }, [openCheckDataModal, setFormValues]);
    return {
        state,
        selectedProfileIdsByPassenger,
        isCheckFfpStatusLoading,
        isCheckDataModalOpen,
        onFillValues,
        onSwapNames,
        onConfirm,
        onModalConfirm,
        closeCheckDataModal
    };
};
